import { useCallback, useEffect, useState } from "react";
import {
  API_ROUTES,
  LIMITS,
  SUDOKU_DIFFICULTY_LABELS,
  formatSudokuTime,
  type AdminOverview,
  type AdminOverviewResponse,
  type AdminUser,
  type BugReport,
  type KoloSpin,
  type PasjansScore,
  type SudokuScore,
  type Game2048Score,
  type UserRole,
  type WordleAdminResetResponse,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { CommentsPanel } from "../components/CommentsPanel.js";
import { Field } from "../components/Field.js";
import { SurveysPanel } from "../components/SurveysPanel.js";
import { CasinoAdminTools } from "../components/admin/CasinoAdminTools.js";
import { KebabAdminTools } from "../components/admin/KebabAdminTools.js";
import { BottleClickerAdminTools } from "../components/admin/BottleClickerAdminTools.js";
import { confirmAdmin } from "../hooks/useIsAdmin.js";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { formatTimestamp } from "../lib/format.js";
import { useFormSubmit } from "../hooks/useFormSubmit.js";
import { useSession } from "../session/SessionProvider.js";
import "../styles/admin.css";

type Tab =
  | "overview"
  | "users"
  | "hub"
  | "pasjans"
  | "kolo"
  | "kebab"
  | "casino"
  | "wordle"
  | "sudoku"
  | "game2048"
  | "bottle"
  | "bugs";

const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: "overview", label: "Przegląd" },
  { id: "users", label: "Konta" },
  { id: "hub", label: "Hub" },
  { id: "pasjans", label: "Pasjans" },
  { id: "kolo", label: "Koło" },
  { id: "kebab", label: "Kebab" },
  { id: "casino", label: "Kasyno" },
  { id: "wordle", label: "Wordle" },
  { id: "sudoku", label: "Sudoku" },
  { id: "game2048", label: "2048" },
  { id: "bottle", label: "Bottle clicker" },
  { id: "bugs", label: "Zgłoszenia" },
];

function confirmDanger(message: string): boolean {
  return confirmAdmin(message);
}

export function AdminPage() {
  const { user, restoring } = useSession();
  const [tab, setTab] = useState<Tab>("overview");

  if (restoring) {
    return <p className="hub-hint">Wczytywanie sesji...</p>;
  }

  if (!user) {
    return (
      <Card title="Panel admina" icon="🛠️">
        <p className="admin-lead">Zaloguj się, żeby otworzyć panel admina.</p>
      </Card>
    );
  }

  if (user.role !== "admin") {
    return (
      <Card title="Panel admina" icon="🛠️">
        <p className="admin-lead">Brak uprawnień.</p>
      </Card>
    );
  }

  return (
    <div className="admin-page">
      <header>
        <h1 className="admin-title">Panel admina</h1>
        <p className="admin-lead">
          Narzędzia do całej strony. Hasło konta <strong>admin</strong> ustawiasz Ty — CLI
          albo <code>ADMIN_PASSWORD</code> przy pierwszym starcie serwera.
        </p>
      </header>

      <nav className="admin-tabs" aria-label="Sekcje panelu">
        {TABS.map((item) => (
          <Button
            key={item.id}
            size="sm"
            aria-pressed={tab === item.id}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </nav>

      {tab === "overview" ? <OverviewSection onOpen={setTab} /> : null}
      {tab === "users" ? <UsersSection currentUserId={user.id} /> : null}
      {tab === "hub" ? (
        <div className="admin-hub">
          <CommentsPanel />
          <SurveysPanel />
        </div>
      ) : null}
      {tab === "pasjans" ? <PasjansSection /> : null}
      {tab === "kolo" ? <KoloSection /> : null}
      {tab === "kebab" ? <KebabAdminTools /> : null}
      {tab === "casino" ? <CasinoAdminTools /> : null}
      {tab === "wordle" ? <WordleSection /> : null}
      {tab === "sudoku" ? <SudokuSection /> : null}
      {tab === "game2048" ? <Game2048Section /> : null}
      {tab === "bottle" ? <BottleClickerAdminTools /> : null}
      {tab === "bugs" ? <BugsSection /> : null}
    </div>
  );
}

function BugsSection() {
  const [rows, setRows] = useState<BugReport[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    const list = await apiRequest<BugReport[]>(API_ROUTES.admin.bugReports);
    setRows(list);
    setError(null);
  }, []);

  useEffect(() => {
    reload().catch((cause: unknown) => setError(errorMessage(cause)));
  }, [reload]);

  async function setStatus(row: BugReport, status: BugReport["status"]) {
    setBusyId(row.id);
    try {
      const updated = await apiRequest<BugReport>(API_ROUTES.admin.bugReport(row.id), {
        method: "PATCH",
        body: { status },
      });
      setRows((current) => current?.map((item) => (item.id === updated.id ? updated : item)) ?? null);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(row: BugReport) {
    if (!confirmAdmin("Usunąć to zgłoszenie?")) return;
    setBusyId(row.id);
    try {
      await apiRequest(API_ROUTES.admin.bugReport(row.id), { method: "DELETE" });
      setRows((current) => current?.filter((item) => item.id !== row.id) ?? null);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p className="hub-error">{error}</p>;
  if (!rows) return <p className="hub-hint">Wczytywanie zgłoszeń...</p>;

  return (
    <Card title="Zgłoszenia" icon="🐛">
      <p className="admin-muted">
        Formularz w stopce. Goście mogą pisać bez konta. Otwarte na górze.
      </p>
      {rows.length === 0 ? (
        <p className="hub-hint">Brak zgłoszeń.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Kto</th>
                <th>Strona</th>
                <th>Treść</th>
                <th>Data</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className={row.status === "done" ? "admin-bug--done" : undefined}>
                  <td>{row.username}</td>
                  <td>
                    <code>{row.page || "—"}</code>
                  </td>
                  <td className="admin-bug-body">{row.body}</td>
                  <td>{formatTimestamp(row.createdAt)}</td>
                  <td>
                    <div className="admin-row-actions">
                      <Button
                        size="sm"
                        disabled={busyId === row.id}
                        onClick={() => void setStatus(row, row.status === "open" ? "done" : "open")}
                      >
                        {row.status === "open" ? "Załatwione" : "Otwórz"}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busyId === row.id}
                        onClick={() => void remove(row)}
                      >
                        Usuń
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function OverviewSection({ onOpen }: { onOpen: (tab: Tab) => void }) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    apiRequest<AdminOverviewResponse>(API_ROUTES.admin.overview, {
      signal: controller.signal,
    })
      .then((body) => {
        setOverview(body.overview);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(errorMessage(cause));
      });
    return () => controller.abort();
  }, []);

  if (error) return <p className="hub-error">{error}</p>;
  if (!overview) return <p className="hub-hint">Wczytywanie...</p>;

  const cards: Array<{ tab: Tab; value: number; label: string }> = [
    { tab: "users", value: overview.users, label: "Konta" },
    { tab: "users", value: overview.admins, label: "Admini" },
    { tab: "hub", value: overview.comments, label: "Komentarze" },
    { tab: "hub", value: overview.surveys, label: "Ankiety" },
    { tab: "pasjans", value: overview.pasjansScores, label: "Pasjans" },
    { tab: "kolo", value: overview.koloSpins, label: "Koło" },
    { tab: "kebab", value: overview.kebabContestants, label: "Zawodnicy kebaba" },
    { tab: "kebab", value: overview.kebabEntries, label: "Wpisy kebaba" },
    { tab: "casino", value: overview.casinoWallets, label: "Portfele kasyna" },
    { tab: "sudoku", value: overview.sudokuScores, label: "Sudoku" },
    { tab: "game2048", value: overview.game2048Scores, label: "2048" },
    { tab: "bottle", value: overview.bottleClickerSaves, label: "Bottle clicker" },
    { tab: "bugs", value: overview.bugReportsOpen, label: "Otwarte zgłoszenia" },
  ];

  return (
    <Card title="Stan strony" icon="📊">
      <div className="admin-stats">
        {cards.map((card) => (
          <button
            key={card.label}
            type="button"
            className="admin-stat"
            onClick={() => onOpen(card.tab)}
          >
            <span className="admin-stat__value">{card.value}</span>
            <span className="admin-stat__label">{card.label}</span>
          </button>
        ))}
      </div>
    </Card>
  );
}

function UsersSection({ currentUserId }: { currentUserId: number }) {
  const { busy, fields, submit } = useFormSubmit();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwords, setPasswords] = useState<Record<number, string>>({});

  const reload = useCallback(async () => {
    const list = await apiRequest<AdminUser[]>(API_ROUTES.admin.users);
    setUsers(list);
    setError(null);
  }, []);

  useEffect(() => {
    reload().catch((cause: unknown) => setError(errorMessage(cause)));
  }, [reload]);

  async function saveUser(user: AdminUser, role: UserRole) {
    const password = passwords[user.id] ?? "";
    const ok = await submit(async () => {
      const body: { role: UserRole; password?: string } = { role };
      if (password) body.password = password;
      const updated = await apiRequest<AdminUser>(API_ROUTES.admin.user(user.id), {
        method: "PATCH",
        body,
      });
      setUsers((current) =>
        current?.map((item) => (item.id === updated.id ? updated : item)) ?? null,
      );
    }, "Zapisano konto");
    if (ok) {
      setPasswords((current) => ({ ...current, [user.id]: "" }));
    }
  }

  async function removeUser(user: AdminUser) {
    if (
      !confirmDanger(
        `Usunąć konto ${user.username}? Komentarze zostaną jako Anonim, portfel kasyna zniknie.`,
      )
    ) {
      return;
    }
    await submit(async () => {
      await apiRequest(API_ROUTES.admin.user(user.id), { method: "DELETE" });
      setUsers((current) => current?.filter((item) => item.id !== user.id) ?? null);
    }, "Usunięto konto");
  }

  if (error) return <p className="hub-error">{error}</p>;
  if (!users) return <p className="hub-hint">Wczytywanie kont...</p>;

  return (
    <Card title="Konta" icon="👤">
      <p className="admin-muted">
        Nazwa <code>admin</code> jest zarezerwowana przy rejestracji. Ostatniego admina nie
        da się usunąć ani zdegradować.
      </p>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nazwa</th>
              <th>Rola</th>
              <th>Nowe hasło</th>
              <th>Od</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                busy={busy}
                password={passwords[user.id] ?? ""}
                passwordError={fields.password}
                canDelete={user.id !== currentUserId}
                onPassword={(value) =>
                  setPasswords((current) => ({ ...current, [user.id]: value }))
                }
                onSave={(role) => void saveUser(user, role)}
                onDelete={() => void removeUser(user)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function UserRow({
  user,
  busy,
  password,
  passwordError,
  canDelete,
  onPassword,
  onSave,
  onDelete,
}: {
  user: AdminUser;
  busy: boolean;
  password: string;
  passwordError?: string | undefined;
  canDelete: boolean;
  onPassword: (value: string) => void;
  onSave: (role: UserRole) => void;
  onDelete: () => void;
}) {
  const [role, setRole] = useState<UserRole>(user.role);

  useEffect(() => {
    setRole(user.role);
  }, [user.role]);

  return (
    <tr>
      <td>
        <strong>{user.username}</strong>
      </td>
      <td>
        <select
          className="z-select"
          value={role}
          onChange={(event) => setRole(event.target.value as UserRole)}
        >
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
      </td>
      <td>
        <Field error={passwordError}>
          <input
            className="z-input"
            type="password"
            autoComplete="new-password"
            placeholder="Bez zmian"
            minLength={LIMITS.password.min}
            maxLength={LIMITS.password.max}
            value={password}
            onChange={(event) => onPassword(event.target.value)}
          />
        </Field>
      </td>
      <td>{formatTimestamp(user.createdAt)}</td>
      <td>
        <div className="admin-row-actions">
          <Button size="sm" disabled={busy} onClick={() => onSave(role)}>
            Zapisz
          </Button>
          {canDelete ? (
            <Button size="sm" variant="danger" disabled={busy} onClick={onDelete}>
              Usuń
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function PasjansSection() {
  return (
    <LeaderboardCard
      title="Pasjans"
      icon="🃏"
      empty="Brak wyników."
      listPath={API_ROUTES.admin.pasjansScores}
      itemPath={API_ROUTES.admin.pasjansScore}
      wipeLabel="Wyczyścić całą tablicę pasjansa?"
      columns={["Gracz", "Wynik", "Ruchy", "Data"]}
      render={(score: PasjansScore) => [
        score.username,
        String(score.score),
        String(score.moves),
        formatTimestamp(score.createdAt),
      ]}
    />
  );
}

function KoloSection() {
  return (
    <LeaderboardCard
      title="Koło fortuny"
      icon="🎡"
      empty="Brak spinów."
      listPath={API_ROUTES.admin.koloSpins}
      itemPath={API_ROUTES.admin.koloSpin}
      wipeLabel="Wyczyścić historię koła?"
      columns={["Kto", "Wygrana", "Opcje", "Data"]}
      render={(spin: KoloSpin) => [
        spin.username,
        spin.winner,
        spin.options.join(", "),
        formatTimestamp(spin.createdAt),
      ]}
    />
  );
}

function SudokuSection() {
  return (
    <LeaderboardCard
      title="Sudoku"
      icon="9️⃣"
      empty="Brak wyników."
      listPath={API_ROUTES.admin.sudokuScores}
      itemPath={API_ROUTES.admin.sudokuScore}
      wipeLabel="Wyczyścić tablicę sudoku?"
      columns={["Gracz", "Poziom", "Wynik", "Czas", "Data"]}
      render={(score: SudokuScore) => [
        score.username,
        SUDOKU_DIFFICULTY_LABELS[score.difficulty],
        String(score.score),
        formatSudokuTime(score.time),
        formatTimestamp(score.createdAt),
      ]}
    />
  );
}

function Game2048Section() {
  return (
    <LeaderboardCard
      title="2048"
      icon="🔢"
      empty="Brak wyników."
      listPath={API_ROUTES.admin.game2048Scores}
      itemPath={API_ROUTES.admin.game2048Score}
      wipeLabel="Wyczyścić tablicę 2048?"
      columns={["Gracz", "Wynik", "Kafel", "Data"]}
      render={(score: Game2048Score) => [
        score.username,
        String(score.score),
        String(score.maxTile),
        formatTimestamp(score.createdAt),
      ]}
    />
  );
}

function LeaderboardCard<T extends { id: number }>({
  title,
  icon,
  empty,
  listPath,
  itemPath,
  wipeLabel,
  columns,
  render,
}: {
  title: string;
  icon: string;
  empty: string;
  listPath: string;
  itemPath: (id: number) => string;
  wipeLabel: string;
  columns: string[];
  render: (row: T) => string[];
}) {
  const { busy, submit } = useFormSubmit();
  const [rows, setRows] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const list = await apiRequest<T[]>(listPath);
    setRows(list);
    setError(null);
  }, [listPath]);

  useEffect(() => {
    reload().catch((cause: unknown) => setError(errorMessage(cause)));
  }, [reload]);

  async function onDelete(id: number) {
    if (!confirmDanger("Usunąć ten wpis?")) return;
    await submit(async () => {
      await apiRequest(itemPath(id), { method: "DELETE" });
      setRows((current) => current?.filter((row) => row.id !== id) ?? null);
    }, "Usunięto");
  }

  async function onWipe() {
    if (!confirmDanger(wipeLabel)) return;
    await submit(async () => {
      await apiRequest(listPath, { method: "DELETE" });
      setRows([]);
    }, "Wyczyszczono");
  }

  return (
    <Card title={title} icon={icon}>
      <div className="admin-toolbar">
        <Button size="sm" variant="danger" disabled={busy} onClick={() => void onWipe()}>
          Wyczyść wszystko
        </Button>
      </div>
      {error ? <p className="hub-error">{error}</p> : null}
      {rows === null ? (
        <p className="hub-hint">Wczytywanie...</p>
      ) : rows.length === 0 ? (
        <p className="hub-hint">{empty}</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column}>{column}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {render(row).map((cell, index) => (
                    <td key={index}>{cell}</td>
                  ))}
                  <td>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() => void onDelete(row.id)}
                    >
                      Usuń
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function WordleSection() {
  const { busy, submit } = useFormSubmit();

  async function reset() {
    if (!confirmDanger("Zresetować wszystkie wyniki Wordle (dzienne i unlimited)?")) return;
    await submit(async () => {
      await apiRequest<WordleAdminResetResponse>(API_ROUTES.admin.wordleReset, {
        method: "POST",
      });
    }, "Zresetowano Wordle");
  }

  return (
    <Card title="Wordle" icon="🟩">
      <p className="admin-lead">Czyści ranking i rundy na serwerze.</p>
      <Button variant="danger" disabled={busy} onClick={() => void reset()}>
        Reset wyników Wordle
      </Button>
    </Card>
  );
}
